import { YoutubeFilled } from "@ant-design/icons";
import { message, Spin } from "antd";
import { useEffect, useState } from "react";
import { studioApi } from "../../api";

interface YoutubeAccount {
  _id: string;
  email: string;
  channel_id: string;
  channel_title: string;
  expiry_date: number;
  createdAt:string;
}

export default function ConnectedYoutubeAccounts() {
  const [accounts, setAccounts] = useState<YoutubeAccount[]>([]);
  const [loading, setLoading] = useState(false);

  const getYoutubeAccounts = async () => {
    try {
      setLoading(true);

      const { data } = await studioApi.get("/youtube");

      setAccounts(data || []);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to fetch YouTube accounts"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getYoutubeAccounts();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spin size="large" />
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20">
        <div className="text-center">
          <div className="mb-3 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10">
              <YoutubeFilled className="text-3xl text-red-600" />
            </div>
          </div>

          <h3 className="text-lg font-semibold text-slate-800">
            No YouTube Accounts Connected
          </h3>

          <p className="mt-2 text-sm text-slate-500">
            Connect your YouTube account to manage livestreams.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="
        grid w-screen
        grid-cols-[repeat(auto-fill,minmax(280px,1fr))]
        gap-5
        items-start
      "
    >
      {accounts.map((account) => {

        return (
          <div
            key={account._id}
            className="
            group
            flex
            h-[250px]
            w-full
            flex-col
            justify-between
            rounded-2xl
            border
            border-slate-200
            bg-white
            p-5
            shadow-sm
            transition-all
            duration-300
            hover:-translate-y-1
            hover:border-red-200
            hover:shadow-xl
          "
          >
            <div>
              <div className="mb-5 flex items-start gap-4">
                <div
                  className="
                    flex
                    h-14
                    w-14
                    flex-shrink-0
                    items-center
                    justify-center
                    rounded-2xl
                    bg-red-500/10
                    transition-all
                    duration-300
                    group-hover:bg-red-500/15
                  "
                >
                  <YoutubeFilled className="text-[28px] text-red-600" />
                </div>

                <div className="min-w-0 flex-1">
                  <h3
                    className="
                      line-clamp-1
                      text-[16px]
                      font-semibold
                      text-slate-900
                    "
                  >
                    {account.channel_title}
                  </h3>

                  <p
                    className="
                      mt-1
                      line-clamp-2
                      text-sm
                      leading-6
                      text-slate-500
                    "
                  >
                    {account.email}
                  </p>
                </div>
              </div>

            
            </div>

            <div className="space-y-4 border-t border-slate-100 pt-4">
              <div>
                <div
                  className="
                    mb-1
                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-wider
                    text-slate-400
                  "
                >
                  Channel ID
                </div>

                <div
                  className="
                    break-all
                    text-xs
                    leading-5
                    text-slate-500
                  "
                >
                  {account.channel_id}
                </div>
              </div>

              <div>
                <div
                  className="
                    mb-1
                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-wider
                    text-slate-400
                  "
                >
                  CREATED AT
                </div>

                <div className="text-xs text-slate-500">
                  {new Date(account.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
